import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { NavController,
    AlertController,
    ModalController
  } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { ToggleService } from '../services/toggle.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { LanguageSwitcherPopupComponent } from '../language-switcher/language-switcher-popup.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
})
export class UserInfoComponent implements OnInit, OnDestroy {
  public error;
  public email;
  public editing = false;
  
  // Language-related properties
  public currentLanguage: string = 'en';
  public currentLanguageInfo: any = { name: 'English', flag: '🇺🇸' };
  private languageSubscription: Subscription;

  constructor(
    private alertController: AlertController,
    private userService: UserService,
    private navCtrl: NavController,
    private toggleService: ToggleService,
    private translate: TranslateService,
    private modalController: ModalController,
    private languageService: LanguageService
  ) {
    this.toggleService.listToggles();
  }

  ngOnInit() {
    this.error = null;
    this.userService.getStoredEmail();
    this.userService.storageUpdate.subscribe((data) => {
      if(data && data['email']) { this.email = (data['email']) }
    });
    
    // Subscribe to language changes
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(language => {
      this.currentLanguage = language;
      this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
    });
    
    // Set initial language
    this.currentLanguage = this.languageService.getCurrentLanguage();
    this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
  }

  ngOnDestroy() {
    // Clean up subscription
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  isToggleEnabled(toggleName: string): boolean {
    return this.toggleService.isToggleEnabled(toggleName);
  }

  trialAvailable() {
    return this.isToggleEnabled("freeToPremium") && !this.userService.accountExpired();
  }

  startTrial() {
    this.navCtrl.navigateForward('start-trial');
  }

  startEditing() {
    this.editing = true;
  }

  cancelEditing() {
    this.editing = false;
  }

  saveChanges() {
    this.editing = false;
  }

  async openLanguageSwitcher() {
    console.log('openLanguageSwitcher', this.isToggleEnabled('internationalization'), this.editing);
    if(this.isToggleEnabled('internationalization') && this.editing) {
      const modal = await this.modalController.create({
        component: LanguageSwitcherPopupComponent,
        componentProps: {},
        cssClass: 'language-switcher-modal'
      });
      
      // Handle modal dismissal to refresh language display
      modal.onDidDismiss().then(() => {
        // Language will be updated automatically via subscription
        // No additional action needed as the language service handles the change
      });
      
      return await modal.present();
    }
  }

  async deleteAccount() {
    const header = await this.translate.get('USER_INFO.DEACTIVATE_CONFIRMATION').toPromise();
    const message = await this.translate.get('USER_INFO.DEACTIVATE_MESSAGE').toPromise();
    const cancelText = await this.translate.get('USER_INFO.CANCEL').toPromise();
    const deactivateText = await this.translate.get('USER_INFO.DEACTIVATE').toPromise();
    const errorMessage = await this.translate.get('USER_INFO.DELETE_ACCOUNT_ERROR').toPromise();
    
    let alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: [
        {
          text: cancelText,
          cssClass: 'primary',
          role: 'cancel'
        }, {
          text: deactivateText,
          role: 'delete',
          cssClass: 'secondary',
          handler: (data) => {
            this.userService.deleteUser()
                .then((data) => { this.navCtrl.navigateForward('home'); })
                .catch(() => { this.error = errorMessage; })
          }
        }
      ]
    });
    alert.present();
  }
}
